import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, HeartIcon, TrashIcon, ExclamationTriangleIcon, CheckCircleIcon, DocumentArrowDownIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { deleteHealthPlanImportById, listHealthPlanImportsByCompetence } from '../services/healthPlanImportsService';
import type { HealthPlanImportDocument, HealthPlanLifeRecord, HealthPlanProvider } from '../services/healthPlanTypes';
import { healthPlanProviderBySlug } from './healthPlanProviders';
import { useCollaboratorsRealtimeQuery } from '@/features/Collaborators/Data';
import { useToast } from '@/hooks/useToast';
import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';
import { normalizeString } from '@/utils/stringUtils';
import { listPayrollImports } from '../services/payrollImportsService';

function formatCurrency(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatCpf(cpf?: string | null): string {
  if (!cpf) return '—';
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

const formatCertificateCode = (value?: string | null): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.includes('/')) return trimmed;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length <= 2) return trimmed;
  const prefix = digits.slice(0, -2);
  const suffix = digits.slice(-2);
  return `${prefix}/${suffix}`;
};

const formatCpfOrCode = (life?: HealthPlanLifeRecord | null): string => {
  if (!life) return '—';
  const normalizedCpf = (life.beneficiaryCpf || '').replace(/\D/g, '');
  if (normalizedCpf.length === 11) {
    return formatCpf(life.beneficiaryCpf);
  }
  const certificateLike =
    formatCertificateCode(life.certificateCode) || formatCertificateCode(life.userCode) || null;
  if (certificateLike) return certificateLike;
  const code =
    life.userCode ||
    life.certificateCode ||
    life.familyIdentifier ||
    (life.beneficiaryCpf ? life.beneficiaryCpf.trim() : '') ||
    life.holderCpf;
  return code && code.trim() ? code.trim() : '—';
};

export function HealthPlanImportDetailsPage() {
  const { competence } = useParams<{ competence: string }>();
  const navigate = useNavigate();
  const { error: showError, success: showSuccess } = useToast();
  const { data: collaborators = [] } = useCollaboratorsRealtimeQuery();
  const [searchParams, setSearchParams] = useSearchParams();

  const [providerFilter, setProviderFilter] = useState<HealthPlanProvider | 'all'>((searchParams.get('provider') as HealthPlanProvider) || 'all');
  const [imports, setImports] = useState<HealthPlanImportDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deduction8111, setDeduction8111] = useState<number>(0);
  const [deduction213, setDeduction213] = useState<number>(0);

  useEffect(() => {
    const fromUrl = searchParams.get('provider') as HealthPlanProvider;
    if (fromUrl && healthPlanProviderBySlug[fromUrl]) {
      setProviderFilter(fromUrl);
    } else {
      setProviderFilter('all');
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      if (!competence) return;
      try {
        setLoading(true);
        setLoadError(null);
        const data = await listHealthPlanImportsByCompetence(competence);
        if (!data.length) {
          setLoadError('Nenhuma importação encontrada para esta competência.');
        }
        setImports(data);
      } catch (err) {
        console.error('[HealthPlanImportDetailsPage] Erro ao carregar import', err);
        const message = 'Não foi possível carregar a importação.';
        setLoadError(message);
        showError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competence]);

  const handleProviderChange = (value: HealthPlanProvider | 'all') => {
    setProviderFilter(value);
    const nextParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      nextParams.delete('provider');
    } else {
      nextParams.set('provider', value);
    }
    setSearchParams(nextParams, { replace: true });
  };

  const filteredImports = useMemo(() => {
    if (providerFilter === 'all') return imports;
    return imports.filter((i) => i.provider === providerFilter);
  }, [imports, providerFilter]);

  type AnnotatedLifeRecord = HealthPlanLifeRecord & { provider?: string; importId?: string };

  const livesPreviewSafe = useMemo<AnnotatedLifeRecord[]>(() => {
    return filteredImports.flatMap((importDoc) => {
      const families = (importDoc as any).familiesPreview;
      if (Array.isArray(families) && families.length) {
        return families.flatMap((family: any) => {
          const people = [
            ...(family?.holder ? [family.holder] : []),
            ...(Array.isArray(family?.dependents) ? family.dependents : []),
          ];
          return people.map((life: any) => ({
            ...life,
            totalCost: life?.totalCost ?? life?.premiumTotal ?? null,
            provider: importDoc.provider,
            importId: importDoc.id,
            collaboratorId: life?.collaboratorId ?? family?.collaboratorId ?? null,
          }));
        });
      }
      return [] as AnnotatedLifeRecord[];
    });
  }, [filteredImports]);
  const copayTotalPreview = livesPreviewSafe.reduce((acc, life) => acc + (life.copayAmount ?? 0), 0);
  const aggregatedTotals = useMemo(() => {
    const collaboratorIds = new Set<string>();
    const totals = filteredImports.reduce(
      (acc, item) => {
        const premium = item.totals?.premiumHealth ?? 0;
        const copay = item.totals?.copayTotal ?? 0;
        const financial = item.totals?.financialCost ?? 0;
        const total = item.totals?.totalCost ?? (premium + copay + financial);

        acc.premiumHealth += premium;
        acc.copayTotal += copay;
        acc.financialCost += financial;
        acc.totalCost += total;

        const families = (item as any).familiesPreview;
        if (Array.isArray(families) && families.length) {
          families.forEach((family: any) => {
            if (family?.collaboratorId) collaboratorIds.add(family.collaboratorId);
            const people = [
              ...(family?.holder ? [family.holder] : []),
              ...(Array.isArray(family?.dependents) ? family.dependents : []),
            ];
            acc.livesCount += people.length;
          });
        }
        return acc;
      },
      { premiumHealth: 0, copayTotal: 0, financialCost: 0, totalCost: 0, livesCount: 0 },
    );
    return { ...totals, collaboratorCount: collaboratorIds.size };
  }, [filteredImports]);

  const totalCostPreview = livesPreviewSafe.reduce((acc, life) => acc + (life.totalCost ?? 0), 0);

  const collaboratorById = useMemo(() => {
    const map = new Map<string, { id: string; name?: string | null }>();
    collaborators.forEach((collab: any) => {
      if (!collab?.id) return;
      map.set(collab.id, collab);
    });
    return map;
  }, [collaborators]);

  const auditItems = useMemo(() => {
    return filteredImports.flatMap((doc) => doc.audit || []);
  }, [filteredImports]);

  useEffect(() => {
    const fetchPayrollDeduction = async () => {
      if (!competence) {
        setDeduction8111(0);
        setDeduction213(0);
        return;
      }
      try {
        const payrollImports = await listPayrollImports();
        const total = payrollImports
          .filter((imp) => (imp.competence ?? '') === competence)
          .reduce(
            (acc, imp) => {
              const byCode = imp.totals?.deduction?.byCode ?? {};
              const val8111 = typeof byCode['8111']?.total === 'number' ? byCode['8111'].total : 0;
              const val213 = typeof byCode['213']?.total === 'number' ? byCode['213'].total : 0;
              acc.d8111 += val8111;
              acc.d213 += val213;
              return acc;
            },
            { d8111: 0, d213: 0 },
          );
        setDeduction8111(total.d8111);
        setDeduction213(total.d213);
      } catch (err) {
        console.error('[HealthPlanImportDetailsPage] Falha ao carregar dedução 8111 da folha', err);
        setDeduction8111(0);
        setDeduction213(0);
      }
    };
    void fetchPayrollDeduction();
  }, [competence]);

  const handleExportPdf = () => {
    const doc = new jsPDF();
    doc.text(`Auditoria de Plano de Saúde - ${competence}`, 14, 10);

    const tableData = auditItems.map((item) => [
      item.beneficiaryName,
      item.provider ? (healthPlanProviderBySlug[item.provider as any]?.name ?? item.provider) : '—',
      formatCurrency(item.premium),
      formatCurrency(item.copay),
      item.reason,
    ]);

    autoTable(doc, {
      head: [['Beneficiário', 'Operadora', 'Prêmio', 'Copart.', 'Motivo']],
      body: tableData,
      startY: 20,
    });

    doc.save(`auditoria-plano-saude-${competence}.pdf`);
  };

  const handleExportCsv = () => {
    const headers = ['Beneficiário', 'CPF', 'Operadora', 'Prêmio', 'Copart.', 'Motivo'];
    const rows = auditItems.map((item) => [
      item.beneficiaryName,
      item.beneficiaryCpf || '',
      item.provider ? (healthPlanProviderBySlug[item.provider as any]?.name ?? item.provider) : '—',
      (item.premium || 0).toFixed(2).replace('.', ','),
      (item.copay || 0).toFixed(2).replace('.', ','),
      item.reason,
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(';')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `auditoria-plano-saude-${competence}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteImport = async (importId: string, label?: string | null) => {
    const competenceLabel = label ? ` ${label}` : '';
    const confirmMessage = `Remover a importação${competenceLabel}? Esta ação não pode ser desfeita.`;
    if (!window.confirm(confirmMessage)) return;
    try {
      setDeletingId(importId);
      await deleteHealthPlanImportById(importId);
      setImports((prev) => prev.filter((importDoc) => importDoc.id !== importId));
      showSuccess('Importação removida com sucesso.');
    } catch (err) {
      console.error('[HealthPlanImportDetailsPage] erro ao remover import', err);
      showError('Não foi possível remover a importação.');
    } finally {
      setDeletingId(null);
    }
  };

  const resolveCollaboratorName = (life?: HealthPlanLifeRecord | null): string | null => {
    if (!life?.collaboratorId) return null;
    const collaborator = collaboratorById.get(life.collaboratorId);
    return collaborator?.name ?? null;
  };

  type LifeGroup = {
    holder: AnnotatedLifeRecord | null;
    dependents: AnnotatedLifeRecord[];
    providers: Set<string>;
    totalPremium?: number;
    totalCopay?: number;
    totalValue?: number;
  };

  const groups: Record<string, LifeGroup> =
    livesPreviewSafe.length > 0
      ? livesPreviewSafe.reduce<Record<string, LifeGroup>>((acc, life, index) => {
        const familyKey = (life.familyIdentifier || '').replace(/\D/g, '');
        const cpfHolder = (life.holderCpf || '').replace(/\D/g, '');
        const cpfBenef = (life.beneficiaryCpf || '').replace(/\D/g, '');
        const key = familyKey || cpfHolder || cpfBenef || `row-${index}`;
        const explicitHolder = life.isPrimaryHolder;
        const isHolder =
          explicitHolder != null
            ? explicitHolder
            : !life.holderCpf ||
            cpfHolder === cpfBenef ||
            normalizeString(life.relationship).includes('tit');
        const current =
          acc[key] ?? {
            holder: null,
            dependents: [],
            providers: new Set<string>(),
          };
        if (life.provider) {
          const providerLabel = healthPlanProviderBySlug[life.provider]?.name ?? life.provider.toUpperCase?.() ?? life.provider;
          current.providers.add(providerLabel);
        }
        if (isHolder && !current.holder) {
          current.holder = life;
        } else {
          current.dependents.push(life);
        }
        acc[key] = current;
        return acc;
      }, {})
      : {};

  const groupsList = Object.values(groups)
    .map((group) => {
      const holder = group.holder ?? group.dependents[0] ?? null;
      const dependentsPremium = group.dependents.reduce((acc, dep) => acc + (dep.premiumTotal ?? 0), 0);
      const dependentsCopay = group.dependents.reduce((acc, dep) => acc + (dep.copayAmount ?? 0), 0);
      const holderPremium = holder?.premiumTotal ?? 0;
      const holderCopay = holder?.copayAmount ?? 0;
      const totalPremium = holderPremium + dependentsPremium;
      const totalCopay = holderCopay + dependentsCopay;
      const totalValue = totalPremium + totalCopay;
      return {
        ...group,
        holder: holder ?? null,
        totalPremium,
        totalCopay,
        totalValue,
      };
    })
    .sort((a, b) => b.totalValue - a.totalValue);

  const totalCostValue =
    aggregatedTotals.totalCost > 0
      ? aggregatedTotals.totalCost
      : livesPreviewSafe.length > 0
        ? totalCostPreview
        : null;

  if (loading) {
    return <div className="text-sm text-gray-600">Carregando importação...</div>;
  }

  if (!imports.length) {
    return (
      <div className="space-y-4">
        <div className="text-sm text-gray-600">{loadError ?? 'Nenhuma importação encontrada para esta competência.'}</div>
        <button
          type="button"
          onClick={() => navigate('/expenses/benefits/health-plan')}
          className="inline-flex items-center gap-2 rounded-md border border-primary-200 bg-white px-3 py-2 text-sm font-semibold text-primary-700 shadow-sm transition hover:-translate-y-0.5 hover:border-primary-300"
        >
          <ArrowLeftIcon className="h-5 w-5" aria-hidden />
          Voltar
        </button>
      </div>
    );
  }

  const importHref =
    providerFilter === 'all'
      ? '/expenses/benefits/health-plan/import'
      : `/expenses/benefits/health-plan/import?provider=${providerFilter}`;

  return (
    <div className="space-y-6">
      <SubPageHeader
        backTo="/expenses/benefits/health-plan"
        icon={<HeartIcon className="h-5 w-5" aria-hidden />}
        badge="Competência"
        title={competence ?? 'Sem competência'}
        description="Resumo das importações registradas."
        actions={
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
              <label htmlFor="provider-filter">Operadora</label>
              <select
                id="provider-filter"
                value={providerFilter}
                onChange={(event) => handleProviderChange((event.target.value as HealthPlanProvider) || 'all')}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:w-48"
              >
                <option value="all">Todos os planos</option>
                {Object.values(healthPlanProviderBySlug).map((provider) => (
                  <option key={provider.slug} value={provider.slug}>
                    {provider.name}
                  </option>
                ))}
              </select>
            </div>
            <Link
              to={importHref}
              className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-primary-700"
            >
              Importar
              <ArrowRightIcon className="h-4 w-4" aria-hidden />
            </Link>
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-6">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase text-gray-500">Valor Total</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900">{formatCurrency(aggregatedTotals.totalCost)}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase text-gray-500">Despesa Financeira</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900">
            {formatCurrency(aggregatedTotals.financialCost ?? 0)}
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase text-gray-500">Prêmio saúde</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900">
            {formatCurrency(aggregatedTotals.premiumHealth ?? 0)}
          </div>
          <div className="mt-1 text-xs font-semibold text-red-600">
            (-) Dedução 8111: {formatCurrency(deduction8111 ?? 0)}
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase text-gray-500">Copay</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900">
            {formatCurrency(aggregatedTotals.copayTotal ?? copayTotalPreview ?? 0)}
          </div>
          <div className="mt-1 text-xs font-semibold text-red-600">
            (-) Dedução 213: {formatCurrency(deduction213 ?? 0)}
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase text-gray-500">Vidas</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900">
            {aggregatedTotals.livesCount || '—'}/{aggregatedTotals.collaboratorCount || '—'}
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase text-gray-500">Prêmio Médio</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900">
            {providerFilter === 'all'
              ? formatCurrency(
                aggregatedTotals.collaboratorCount > 0
                  ? (aggregatedTotals.totalCost -
                    aggregatedTotals.financialCost -
                    (deduction8111 ?? 0) -
                    (deduction213 ?? 0)) /
                  aggregatedTotals.collaboratorCount
                  : 0,
              )
              : '—'}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Importações desta competência</h2>
            <p className="text-sm text-gray-600">Prêmio, copay e vidas por arquivo.</p>
          </div>
        </div>
        <div className="mt-4 overflow-hidden rounded-md border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">Operadora</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">Prêmio</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">Desp. Financ.</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">Copay</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">Total</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">Vidas/Colabs</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {filteredImports.map((item) => {
                const providerName = healthPlanProviderBySlug[item.provider]?.name ?? item.provider?.toUpperCase();
                const totalValue =
                  item.totals?.totalCost ?? (item.totals?.premiumHealth ?? 0) + (item.totals?.copayTotal ?? 0);
                const families = (item as any).familiesPreview;
                const collaboratorCount = Array.isArray(families) ? families.length : null;
                const livesCount = Array.isArray(families)
                  ? families.reduce((acc: number, family: any) => {
                    const people = [
                      ...(family?.holder ? [family.holder] : []),
                      ...(Array.isArray(family?.dependents) ? family.dependents : []),
                    ];
                    return acc + people.length;
                  }, 0)
                  : item.totals?.activeLives ?? null;

                return (
                  <tr key={`import-row-${item.id}`}>
                    <td className="px-4 py-3 text-gray-900">{providerName ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-700">{formatCurrency(item.totals?.premiumHealth ?? 0)}</td>
                    <td className="px-4 py-3 text-gray-700">{formatCurrency(item.totals?.financialCost ?? 0)}</td>
                    <td className="px-4 py-3 text-gray-700">{formatCurrency(item.totals?.copayTotal ?? 0)}</td>
                    <td className="px-4 py-3 text-gray-700">{formatCurrency(totalValue ?? 0)}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {livesCount != null || collaboratorCount != null ? (
                        <>{livesCount ?? '—'}/{collaboratorCount ?? '—'}</>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleDeleteImport(item.id, item.billingPeriod)}
                        className="inline-flex items-center gap-1 rounded-md bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={deletingId === item.id}
                      >
                        <TrashIcon className="h-4 w-4" aria-hidden />
                        {deletingId === item.id ? 'Removendo...' : 'Remover'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white shadow sm:rounded-lg overflow-hidden border border-gray-200">
        <div
          className={`px-4 py-5 sm:px-6 border-b ${auditItems.length > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
            }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3
                className={`text-lg leading-6 font-medium flex items-center gap-2 ${auditItems.length > 0 ? 'text-red-800' : 'text-green-800'
                  }`}
              >
                {auditItems.length > 0 ? (
                  <ExclamationTriangleIcon className="h-5 w-5" />
                ) : (
                  <CheckCircleIcon className="h-5 w-5" />
                )}
                Auditoria Global ({auditItems.length})
              </h3>
              <p className={`mt-1 max-w-2xl text-sm ${auditItems.length > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {auditItems.length > 0
                  ? 'Inconsistências encontradas em todos os arquivos importados para esta competência.'
                  : 'Não foram encontradas pendências de auditoria no arquivo.'}
              </p>
            </div>
            {auditItems.length > 0 && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleExportCsv}
                  className="inline-flex items-center gap-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  <TableCellsIcon className="h-4 w-4 text-gray-500" />
                  CSV
                </button>
                <button
                  type="button"
                  onClick={handleExportPdf}
                  className="inline-flex items-center gap-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  <DocumentArrowDownIcon className="h-4 w-4 text-gray-500" />
                  PDF
                </button>
              </div>
            )}
          </div>
        </div>
        {auditItems.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Beneficiário
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Operadora
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Prêmio
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Copart.
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Motivo
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auditItems.map((item, idx) => (
                  <tr key={idx}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.beneficiaryName}
                      <div className="text-xs text-gray-500">{formatCpf(item.beneficiaryCpf)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.provider ? (healthPlanProviderBySlug[item.provider as any]?.name ?? item.provider) : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatCurrency(item.premium)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatCurrency(item.copay)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                      {item.reason}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Colaboradores</h2>
            <p className="text-sm text-gray-600">Titulares importados, premiação e coparticipação.</p>
          </div>
        </div>
        {groupsList.length === 0 ? (
          <p className="mt-4 text-sm text-gray-600">
            {imports.length
              ? 'Nenhuma vida registrada nesta competência.'
              : 'Nenhuma importação encontrada para esta competência.'}
          </p>
        ) : (
          <div className="mt-4 overflow-hidden rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Beneficiário</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Provedor</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">CPF / Código</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Plano</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Prêmio</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Copay</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Total</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Dependentes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {groupsList.map((group, idx) => {
                  const holder = group.holder ?? group.dependents[0];
                  const dependentsCount = group.dependents.length;
                  const collaboratorName = resolveCollaboratorName(holder);
                  const displayName = collaboratorName ?? holder?.beneficiaryName ?? 'Titular';
                  const providersLabel = Array.from(group.providers ?? []).join(', ') || '—';
                  return (
                    <tr key={`group-${idx}`}>
                      <td className="px-4 py-3 text-gray-900">
                        {holder?.collaboratorId ? (
                          <Link
                            to={
                              holder.importId
                                ? `/expenses/health-plan/${holder.importId}/collaborator/${holder.collaboratorId}`
                                : '/expenses/benefits/health-plan'
                            }
                            className="text-primary-700 underline underline-offset-2 hover:text-primary-800"
                          >
                            {displayName}
                          </Link>
                        ) : (
                          displayName
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{providersLabel}</td>
                      <td className="px-4 py-3 text-gray-700">{formatCpfOrCode(holder)}</td>
                      <td className="px-4 py-3 text-gray-700">{holder?.planName ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-700">{formatCurrency(group.totalPremium)}</td>
                      <td className="px-4 py-3 text-gray-700">{formatCurrency(group.totalCopay)}</td>
                      <td className="px-4 py-3 text-gray-700">{formatCurrency(group.totalValue)}</td>
                      <td className="px-4 py-3 text-gray-700">{dependentsCount}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="text-xs text-gray-500">
        <Link to="/expenses/benefits/health-plan" className="text-primary-700 underline underline-offset-2">
          Voltar para importações
        </Link>
      </div>
    </div>
  );
}
